/**
 * Typed GraphQL operations for the brands feature.
 *
 * Every document below is checked against the backend schema at type-check time
 * (see `src/lib/graphql.ts`). The exported async functions are the data layer the
 * portal calls instead of mutating the in-memory mock in `src/data/config.ts` —
 * each returns a fully-typed result inferred from its selection set.
 */

import { graphql, gqlRequest, type ResultOf } from '@/lib/graphql';
import { makeTheme, type Brand, type Theme, type Terms } from '@/data/config';

// --- Reusable selections -----------------------------------------------------

const ThemeFields = graphql(`
  fragment ThemeFields on Theme {
    id
    name
    header
    background
    surface
    primary
    accent
    text
    logoText
    logoImg
  }
`);

const BrandFields = graphql(
  `
  fragment BrandFields on Brand {
    id
    name
    initials
    status
    viewerRole
    createdAt
    updatedAt
    terminology {
      currencySingular
      currencyPlural
      currencyIcon
      walletLabel
      rewardLabel
    }
    activeTheme {
      ...ThemeFields
    }
    themes {
      ...ThemeFields
    }
    members {
      id
      role
      user {
        id
        public {
          displayName
          username
          photoURL
        }
      }
    }
  }
`,
  [ThemeFields],
);

/** A brand with its themes, terminology and members. */
export type BrandData = ResultOf<typeof BrandFields>;
/** A single theme. */
export type ThemeData = ResultOf<typeof ThemeFields>;

// --- Queries -----------------------------------------------------------------

const MyBrandsQuery = graphql(
  `
  query MyBrands($first: Int, $offset: Int) {
    myBrands(first: $first, offset: $offset) {
      totalCount
      nodes {
        ...BrandFields
      }
    }
  }
`,
  [BrandFields],
);

/** Brands the signed-in user can edit (drives the brand switcher). */
export async function fetchMyBrands(vars: { first?: number; offset?: number } = {}) {
  const data = await gqlRequest(MyBrandsQuery, { first: vars.first ?? null, offset: vars.offset ?? null });
  return data.myBrands;
}

const BrandQuery = graphql(
  `
  query Brand($id: ID!) {
    brand(id: $id) {
      ...BrandFields
    }
  }
`,
  [BrandFields],
);

/** A single brand by public id. Null if the caller is not a member. */
export async function fetchBrand(id: string): Promise<BrandData | null> {
  const data = await gqlRequest(BrandQuery, { id });
  return data.brand;
}

const AdminAllBrandsQuery = graphql(
  `
  query AdminAllBrands($first: Int, $offset: Int) {
    admin_allBrands(first: $first, offset: $offset) {
      totalCount
      nodes {
        ...BrandFields
      }
    }
  }
`,
  [BrandFields],
);

/** Every brand in the database (ADMIN ONLY), ordered by name. Drives the admin
 *  "All brands" directory — distinct from {@link fetchMyBrands}, which only
 *  returns brands the signed-in user is a member of. */
export async function fetchAllBrands(
  vars: { first?: number; offset?: number } = {},
): Promise<{ nodes: BrandData[]; totalCount: number }> {
  const data = await gqlRequest(AdminAllBrandsQuery, {
    first: vars.first ?? 100,
    offset: vars.offset ?? 0,
  });
  return data.admin_allBrands;
}

// --- Brand mutations ---------------------------------------------------------

const CreateBrandMutation = graphql(
  `
  mutation CreateBrand($input: CreateBrandInput!) {
    createBrand(input: $input) {
      brand {
        ...BrandFields
      }
    }
  }
`,
  [BrandFields],
);

export interface TerminologyInput {
  currencySingular: string;
  currencyPlural: string;
  currencyIcon: string;
  walletLabel: string;
  rewardLabel: string;
}

/** A complete starting colour palette for a new brand's default theme. */
export interface StartingPaletteInput {
  header: string;
  background: string;
  surface: string;
  primary: string;
  accent: string;
  text: string;
}

export async function createBrand(input: {
  name: string;
  initials: string;
  terminology?: TerminologyInput;
  /** Colours for the default theme. Omit to use the server's default palette. */
  startingPalette?: StartingPaletteInput;
}): Promise<BrandData> {
  const data = await gqlRequest(CreateBrandMutation, {
    input: {
      name: input.name,
      initials: input.initials,
      terminology: input.terminology ?? null,
      startingPalette: input.startingPalette ?? null,
    },
  });
  return data.createBrand.brand;
}

const UpdateBrandMutation = graphql(
  `
  mutation UpdateBrand($input: UpdateBrandInput!) {
    updateBrand(input: $input) {
      brand {
        ...BrandFields
      }
    }
  }
`,
  [BrandFields],
);

export async function updateBrand(input: {
  id: string;
  name?: string;
  initials?: string;
  status?: 'DRAFT' | 'ACTIVE';
  terminology?: TerminologyInput;
}): Promise<BrandData> {
  const data = await gqlRequest(UpdateBrandMutation, {
    input: {
      id: input.id,
      name: input.name ?? null,
      initials: input.initials ?? null,
      status: input.status ?? null,
      terminology: input.terminology ?? null,
    },
  });
  return data.updateBrand.brand;
}

const DeleteBrandMutation = graphql(`
  mutation DeleteBrand($id: ID!) {
    deleteBrand(id: $id) {
      deletedId
    }
  }
`);

export async function deleteBrand(id: string): Promise<string> {
  const data = await gqlRequest(DeleteBrandMutation, { id });
  return data.deleteBrand.deletedId;
}

// --- Theme mutations ---------------------------------------------------------

/** The whole content of a theme (colour tokens nullable; null = app default). */
export interface ThemeContentInput {
  name?: string | null;
  header?: string | null;
  background?: string | null;
  surface?: string | null;
  primary?: string | null;
  accent?: string | null;
  text?: string | null;
  logoText: string;
  logoImg?: string | null;
  /**
   * The full Theme Studio recipe as JSON (treatment · seed · polarity · font),
   * additive provenance alongside the authoritative 6 hexes (WS3.1 / #4827). This
   * is a portal-local field: it is NOT named in any gql document, so it never
   * reaches the wire unless the backend is known to support it — see
   * `theme-capabilities.ts` and the command layer's `toContentInput`. Omit (never
   * `null`) to leave any stored recipe untouched.
   */
  treatment?: string | null;
}

const CreateThemeMutation = graphql(
  `
  mutation CreateTheme($input: CreateThemeInput!) {
    createTheme(input: $input) {
      theme {
        ...ThemeFields
      }
    }
  }
`,
  [ThemeFields],
);

export async function createTheme(input: {
  brandId: string;
  name?: string;
  content?: ThemeContentInput;
}): Promise<ThemeData> {
  const data = await gqlRequest(CreateThemeMutation, {
    input: { brandId: input.brandId, name: input.name ?? null, content: input.content ?? null },
  });
  return data.createTheme.theme;
}

const UpdateThemeMutation = graphql(
  `
  mutation UpdateTheme($input: UpdateThemeInput!) {
    updateTheme(input: $input) {
      theme {
        ...ThemeFields
      }
    }
  }
`,
  [ThemeFields],
);

export async function updateTheme(input: {
  brandId: string;
  themeId: string;
  content: ThemeContentInput;
}): Promise<ThemeData> {
  const data = await gqlRequest(UpdateThemeMutation, {
    input: { brandId: input.brandId, themeId: input.themeId, content: input.content },
  });
  return data.updateTheme.theme;
}

const DeleteThemeMutation = graphql(`
  mutation DeleteTheme($brandId: ID!, $themeId: ID!) {
    deleteTheme(brandId: $brandId, themeId: $themeId) {
      deletedId
    }
  }
`);

export async function deleteTheme(brandId: string, themeId: string): Promise<string> {
  const data = await gqlRequest(DeleteThemeMutation, { brandId, themeId });
  return data.deleteTheme.deletedId;
}

const SetActiveThemeMutation = graphql(
  `
  mutation SetActiveTheme($brandId: ID!, $themeId: ID!) {
    setActiveTheme(brandId: $brandId, themeId: $themeId) {
      brand {
        ...BrandFields
      }
    }
  }
`,
  [BrandFields],
);

export async function setActiveTheme(brandId: string, themeId: string): Promise<BrandData> {
  const data = await gqlRequest(SetActiveThemeMutation, { brandId, themeId });
  return data.setActiveTheme.brand;
}

const PublishBrandThemeMutation = graphql(
  `
  mutation PublishBrandTheme($input: PublishBrandThemeInput!) {
    publishBrandTheme(input: $input) {
      brand {
        ...BrandFields
      }
    }
  }
`,
  [BrandFields],
);

/** The "Publish" action: write theme content + terminology, flip status active. */
export async function publishBrandTheme(input: {
  brandId: string;
  themeId: string;
  content: ThemeContentInput;
  terminology: TerminologyInput;
}): Promise<BrandData> {
  const data = await gqlRequest(PublishBrandThemeMutation, { input });
  return data.publishBrandTheme.brand;
}

/** A six-token palette suggested by the backend from a webpage's brand colours. */
export interface GeneratedPalette {
  header: string;
  background: string;
  surface: string;
  primary: string;
  accent: string;
  text: string;
}

/**
 * Why a palette generation did or didn't produce colours. `SUCCESS` carries a
 * palette; the others explain why the URL couldn't be turned into one. Mirrors
 * the backend `ThemePaletteStatus` enum.
 */
export type GenerateThemeColorsStatus = "SUCCESS" | "PAGE_UNREADABLE" | "PAYWALL" | "UNSAFE" | "NO_PALETTE";

/**
 * Best-effort result of {@link generateThemeColorsFromUrl}. On `SUCCESS`,
 * `palette` holds the six tokens and `message` is null. Otherwise `palette` is
 * null and `message` is a partner-facing explanation to surface.
 */
export interface GenerateThemeColorsResult {
  status: GenerateThemeColorsStatus;
  palette: GeneratedPalette | null;
  message: string | null;
}

const GenerateThemeColorsFromUrlMutation = graphql(`
  mutation GenerateThemeColorsFromUrl($input: GenerateThemeColorsFromUrlInput!) {
    generateThemeColorsFromUrl(input: $input) {
      status
      message
      palette {
        header
        background
        surface
        primary
        accent
        text
      }
    }
  }
`);

/**
 * Ask the backend to read a webpage and suggest a theme palette from its brand
 * colours. Read-only: persists nothing — the caller applies the result to the
 * open draft and the partner saves via the normal publish flow. Best-effort:
 * when the page can't be read or yields no usable colours, the result carries a
 * non-`SUCCESS` status and a `message` instead of a palette.
 */
export async function generateThemeColorsFromUrl(input: {
  brandId: string;
  url: string;
}): Promise<GenerateThemeColorsResult> {
  const data = await gqlRequest(GenerateThemeColorsFromUrlMutation, { input });
  return data.generateThemeColorsFromUrl;
}

const UploadThemeLogoMutation = graphql(`
  mutation UploadThemeLogo($input: UploadThemeLogoInput!) {
    uploadThemeLogo(input: $input) {
      logoUrl
    }
  }
`);

/**
 * Upload a logo image (as a base64 data URL) to cloud storage and get back its
 * hosted URL. Called just before {@link publishBrandTheme} so the bytes are stored
 * out of band; the returned URL is what gets persisted as the theme's `logoImg`.
 */
export async function uploadThemeLogo(input: {
  brandId: string;
  themeId: string;
  image: string;
}): Promise<string> {
  const data = await gqlRequest(UploadThemeLogoMutation, { input });
  return data.uploadThemeLogo.logoUrl;
}

// --- Membership mutations ----------------------------------------------------

const AddBrandMemberMutation = graphql(`
  mutation AddBrandMember($input: AddBrandMemberInput!) {
    addBrandMember(input: $input) {
      member {
        id
        role
        user {
          id
          public {
            displayName
            username
            photoURL
          }
        }
      }
    }
  }
`);

export async function addBrandMember(input: {
  brandId: string;
  username: string;
  role?: 'OWNER' | 'MEMBER';
}) {
  const data = await gqlRequest(AddBrandMemberMutation, {
    input: { brandId: input.brandId, username: input.username, role: input.role ?? null },
  });
  return data.addBrandMember.member;
}

const UpdateBrandMemberRoleMutation = graphql(`
  mutation UpdateBrandMemberRole($input: UpdateBrandMemberRoleInput!) {
    updateBrandMemberRole(input: $input) {
      member {
        id
        role
      }
    }
  }
`);

export async function updateBrandMemberRole(input: {
  brandId: string;
  memberId: string;
  role: 'OWNER' | 'MEMBER';
}) {
  const data = await gqlRequest(UpdateBrandMemberRoleMutation, { input });
  return data.updateBrandMemberRole.member;
}

const RemoveBrandMemberMutation = graphql(`
  mutation RemoveBrandMember($brandId: ID!, $memberId: ID!) {
    removeBrandMember(brandId: $brandId, memberId: $memberId) {
      deletedId
    }
  }
`);

export async function removeBrandMember(brandId: string, memberId: string): Promise<string> {
  const data = await gqlRequest(RemoveBrandMemberMutation, { brandId, memberId });
  return data.removeBrandMember.deletedId;
}

// --- Mappers (API <-> UI shapes) ---------------------------------------------
//
// The portal's components consume the `Brand`/`Theme`/`Terms` types from
// `config.ts`. The backend's colour tokens are nullable (null = "fall back to the
// app default"), while the UI always renders concrete colours, so we substitute
// the default palette for any null token on the way in.

/** Map a server `ThemeData` to the UI `Theme` (null colours → default palette). */
export function toUiTheme(theme: ThemeData): Theme {
  const fallback = makeTheme();
  return {
    id: theme.id,
    name: theme.name,
    header: theme.header ?? fallback.header,
    background: theme.background ?? fallback.background,
    surface: theme.surface ?? fallback.surface,
    primary: theme.primary ?? fallback.primary,
    accent: theme.accent ?? fallback.accent,
    text: theme.text ?? fallback.text,
    logoText: theme.logoText,
    logoImg: theme.logoImg ?? null,
  };
}

/** Map a server `BrandData` to the UI `Brand`. */
export function toUiBrand(brand: BrandData): Brand {
  const themes = brand.themes.map(toUiTheme);
  const owner = brand.members.find((m) => m.role === 'OWNER');
  return {
    id: brand.id,
    name: brand.name,
    initials: brand.initials,
    plan: brand.status === 'ACTIVE' ? 'White-label · Active' : 'White-label · Draft',
    themes,
    activeThemeId: brand.activeTheme?.id ?? themes[0]?.id ?? '',
    viewerRole: brand.viewerRole ?? null,
    ownerName: owner ? owner.user.public?.displayName || owner.user.public?.username || null : null,
    terms: {
      currencySingular: brand.terminology.currencySingular,
      currencyPlural: brand.terminology.currencyPlural,
      currencyIcon: brand.terminology.currencyIcon,
      walletLabel: brand.terminology.walletLabel,
      rewardLabel: brand.terminology.rewardLabel,
    },
  };
}

/** A brand membership, flattened for the admin members UI. */
export interface AdminMember {
  id: string;
  role: 'OWNER' | 'MEMBER';
  name: string;
  username: string;
  photoURL: string;
}

/** A row in the admin "All brands" directory: the UI brand plus the admin-only
 *  metadata (timestamps, counts, members) the directory and members dialog need.
 *  Keeps the raw `BrandData` so opening the brand reuses {@link toUiBrand}. */
export interface AdminBrandRow {
  id: string;
  name: string;
  initials: string;
  status: 'DRAFT' | 'ACTIVE';
  viewerRole: 'OWNER' | 'MEMBER' | null;
  themeCount: number;
  members: AdminMember[];
  createdAt: number;
  updatedAt: number;
  /** The untouched server brand, for handing to {@link toUiBrand} on "Open". */
  raw: BrandData;
}

/** Flatten a server `BrandMember` selection to the admin members UI shape. */
export function toAdminMember(member: BrandData['members'][number]): AdminMember {
  return {
    id: member.id,
    role: member.role,
    name: member.user.public?.displayName || member.user.public?.username || 'Unknown user',
    username: member.user.public?.username ?? '',
    photoURL: member.user.public?.photoURL ?? '',
  };
}

/** Map a server `BrandData` to an admin directory row. */
export function toAdminBrandRow(brand: BrandData): AdminBrandRow {
  return {
    id: brand.id,
    name: brand.name,
    initials: brand.initials,
    status: brand.status,
    viewerRole: brand.viewerRole ?? null,
    themeCount: brand.themes.length,
    members: brand.members.map(toAdminMember),
    createdAt: brand.createdAt,
    updatedAt: brand.updatedAt,
    raw: brand,
  };
}

/** Map a UI `Theme` to the whole-theme write input. */
export function toThemeContentInput(theme: Theme): ThemeContentInput {
  return {
    name: theme.name,
    header: theme.header,
    background: theme.background,
    surface: theme.surface,
    primary: theme.primary,
    accent: theme.accent,
    text: theme.text,
    logoText: theme.logoText,
    logoImg: theme.logoImg,
  };
}

/** Map UI `Terms` to the terminology input. */
export function toTerminologyInput(terms: Terms): TerminologyInput {
  return {
    currencySingular: terms.currencySingular,
    currencyPlural: terms.currencyPlural,
    currencyIcon: terms.currencyIcon,
    walletLabel: terms.walletLabel,
    rewardLabel: terms.rewardLabel,
  };
}
